import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Card,
  Button,
  Alert,
  Spin,
  Radio,
  Form,
  Input,
  message,
  Steps,
} from "antd";
import {
  CreditCardOutlined,
  BankOutlined,
  DollarOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { PAYMENT_METHOD } from "../../lib/api/paymentApi";
import {
  createRentPaymentIntent,
  processRentPayment,
} from "../../lib/payments/stripe";
import { trackEvent } from "../../helpers/mixpanel";
import StripeTrustBadge from "../StripeTrustBadge";

// Helper function to get auth token from Firebase
const getAuthToken = async () => {
  try {
    const { getAuth } = await import("firebase/auth");
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const token = await user.getIdToken();
    return token;
  } catch (error) {
    console.error("Error getting auth token:", error);
    throw new Error("Failed to get authentication token");
  }
};

const { Step } = Steps;

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentForm = ({ invoiceId, amount, onSuccess, onError }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHOD.CARD);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);

  // Load bank accounts on component mount
  useEffect(() => {
    loadBankAccounts();
  }, []);

  const loadBankAccounts = async () => {
    try {
      const API_BASE_URL =
        process.env.REACT_APP_API_URL || "http://localhost:4000/api";
      const token = await getAuthToken();

      // Resolve the tenant's uid to build the Plaid path.
      const { getAuth } = await import("firebase/auth");
      const tenantId = getAuth().currentUser?.uid;
      if (!tenantId) throw new Error("User not authenticated");

      const response = await fetch(
        `${API_BASE_URL}/plaid/tenants/${tenantId}/bank-accounts`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const bankAccounts = await response.json();
      setBankAccounts(bankAccounts);
    } catch (err) {
      console.error("Error loading bank accounts:", err);
      setBankAccounts([]);
    }
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    setError(null);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setError(null);
      setCurrentStep(2); // Move to processing step

      let paymentData = {
        amount: amount,
        currency: "usd",
        description: `Rent payment for invoice ${invoiceId}`,
        ...values,
      };

      let response;

      if (paymentMethod === PAYMENT_METHOD.CARD) {
        // Create payment intent
        const paymentIntent = await createRentPaymentIntent(amount, "usd", {
          invoiceId,
          tenantId: values.tenantId || "current_tenant",
          unitId: values.unitId || "current_unit",
          description: paymentData.description,
        });

        // Process payment
        response = await processRentPayment(
          values.paymentMethodId,
          paymentIntent.id,
          amount,
          paymentIntent.metadata
        );
      } else {
        // For ACH payments, we'll use a simplified approach for now
        // In a real implementation, this would integrate with Plaid or similar
        response = {
          success: true,
          data: {
            paymentId: `ach_${Date.now()}`,
            status: "processing",
          },
        };
      }

      if (response.success || response.status === "succeeded") {
        setCurrentStep(3); // Success
        onSuccess(response.data?.paymentId || response.id);

        try {
          trackEvent("payment_completed", {
            invoiceId,
            amount,
            method: paymentMethod,
            paymentId: response.data?.paymentId || response.id,
          });
        } catch (err) {
          console.warn("Failed to track event:", err);
        }
      } else {
        throw new Error(response.error || "Payment failed");
      }
    } catch (err) {
      setError(err.message || "Payment failed. Please try again.");
      onError(err.message || "Payment failed");
      setCurrentStep(1); // Go back to payment details

      try {
        trackEvent("payment_failed", {
          invoiceId,
          amount,
          method: paymentMethod,
          error: err.message,
        });
      } catch (err) {
        console.warn("Failed to track event:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Payment Method Selection
        return (
          <PaymentMethodStep>
            <StepTitle>Choose Payment Method</StepTitle>
            <StepDescription>
              Select how you'd like to pay your rent. Both options are secure
              and processed instantly.
            </StepDescription>

            <PaymentMethodOption
              selected={paymentMethod === PAYMENT_METHOD.ACH}
              onClick={() => handlePaymentMethodChange(PAYMENT_METHOD.ACH)}
              disabled={bankAccounts.length === 0}
            >
              <PaymentMethodIcon>
                <BankOutlined />
              </PaymentMethodIcon>
              <PaymentMethodInfo>
                <PaymentMethodName>Bank Transfer (ACH)</PaymentMethodName>
                <PaymentMethodDescription>
                  {bankAccounts.length > 0
                    ? `Pay from your linked bank account`
                    : "Link a bank account to enable ACH payments"}
                </PaymentMethodDescription>
              </PaymentMethodInfo>
            </PaymentMethodOption>

            <PaymentMethodOptions>
              <PaymentMethodOption
                selected={paymentMethod === PAYMENT_METHOD.CARD}
                onClick={() => handlePaymentMethodChange(PAYMENT_METHOD.CARD)}
              >
                <PaymentMethodIcon>
                  <CreditCardOutlined />
                </PaymentMethodIcon>
                <PaymentMethodInfo>
                  <PaymentMethodName>Credit/Debit Card</PaymentMethodName>
                  <PaymentMethodDescription>
                    Pay instantly with any major credit or debit card
                  </PaymentMethodDescription>
                </PaymentMethodInfo>
              </PaymentMethodOption>
            </PaymentMethodOptions>

            <ActionButton
              type="primary"
              size="large"
              onClick={() => setCurrentStep(1)}
              disabled={
                paymentMethod === PAYMENT_METHOD.ACH &&
                bankAccounts.length === 0
              }
            >
              Continue
            </ActionButton>
          </PaymentMethodStep>
        );

      case 1: // Payment Details
        return (
          <PaymentDetailsStep>
            <StepTitle>Payment Details</StepTitle>
            <StepDescription>
              {paymentMethod === PAYMENT_METHOD.CARD
                ? "Enter your card information to complete the payment."
                : "Select your bank account and confirm the payment."}
            </StepDescription>

            <PaymentAmount>
              <AmountLabel>Payment Amount</AmountLabel>
              <AmountValue>${amount.toFixed(2)}</AmountValue>
            </PaymentAmount>

            {paymentMethod === PAYMENT_METHOD.CARD ? (
              <CardPaymentForm
                onSubmit={handleSubmit}
                loading={loading}
                amount={amount}
              />
            ) : (
              <AchPaymentForm
                bankAccounts={bankAccounts}
                selectedBankAccount={selectedBankAccount}
                onBankAccountSelect={setSelectedBankAccount}
                onSubmit={handleSubmit}
                loading={loading}
                amount={amount}
              />
            )}
          </PaymentDetailsStep>
        );

      case 2: // Processing
        return (
          <ProcessingStep>
            <ProcessingContent>
              <Spin size="large" />
              <ProcessingText>Processing your payment...</ProcessingText>
              <ProcessingDescription>
                Please don't close this window while we process your payment.
              </ProcessingDescription>
            </ProcessingContent>
          </ProcessingStep>
        );

      case 3: // Success
        return (
          <SuccessStep>
            <SuccessContent>
              <CheckCircleOutlined className="success-icon" />
              <SuccessTitle>Payment Successful!</SuccessTitle>
              <SuccessDescription>
                Your rent payment has been processed successfully. You'll
                receive a confirmation email shortly.
              </SuccessDescription>

              <PaymentSummary>
                <SummaryItem>
                  <strong>Amount:</strong> ${amount.toFixed(2)}
                </SummaryItem>
                <SummaryItem>
                  <strong>Method:</strong>{" "}
                  {paymentMethod === PAYMENT_METHOD.CARD ? "Card" : "ACH"}
                </SummaryItem>
                <SummaryItem>
                  <strong>Date:</strong> {new Date().toLocaleDateString()}
                </SummaryItem>
              </PaymentSummary>
            </SuccessContent>
          </SuccessStep>
        );

      default:
        return null;
    }
  };

  return (
    <PaymentFormCard>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: "1rem" }}
        />
      )}

      <Steps current={currentStep} style={{ marginBottom: "2rem" }}>
        <Step title="Method" description="Choose payment method" />
        <Step title="Details" description="Enter payment details" />
        <Step title="Processing" description="Processing payment" />
        <Step title="Complete" description="Payment successful" />
      </Steps>

      {renderStepContent()}
    </PaymentFormCard>
  );
};

// Card Payment Form Component
const CardPaymentForm = ({ onSubmit, loading, amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [form] = Form.useForm();

  const handleSubmit = async (values) => {
    if (!stripe || !elements) {
      throw new Error("Stripe not loaded");
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      throw new Error("Card element not found");
    }

    // Create payment method
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
    });

    if (error) {
      throw new Error(error.message);
    }

    // Submit with payment method ID
    await onSubmit({
      ...values,
      paymentMethodId: paymentMethod.id,
    });
  };

  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical">
      <Form.Item label="Card Information" required>
        <CardElementContainer>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  "::placeholder": {
                    color: "#aab7c4",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
            }}
          />
          <StripeTrustBadge variant="checkout" />
        </CardElementContainer>
      </Form.Item>

      <Form.Item
        label="Name on Card"
        name="name"
        rules={[
          { required: true, message: "Please enter the name on your card" },
        ]}
      >
        <Input placeholder="John Smith" />
      </Form.Item>

      <Form.Item
        label="Email"
        name="email"
        rules={[
          { required: true, message: "Please enter your email" },
          { type: "email", message: "Please enter a valid email" },
        ]}
      >
        <Input placeholder="john@example.com" />
      </Form.Item>

      <ActionButton
        type="primary"
        size="large"
        htmlType="submit"
        loading={loading}
        disabled={!stripe}
      >
        Pay ${amount.toFixed(2)}
      </ActionButton>
    </Form>
  );
};

// ACH Payment Form Component
const AchPaymentForm = ({
  bankAccounts,
  selectedBankAccount,
  onBankAccountSelect,
  onSubmit,
  loading,
  amount,
}) => {
  const [form] = Form.useForm();

  const handleSubmit = async (values) => {
    if (!selectedBankAccount) {
      throw new Error("Please select a bank account");
    }

    await onSubmit({
      ...values,
      bankAccountId: selectedBankAccount,
    });
  };

  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical">
      <Form.Item label="Select Bank Account" required>
        <BankAccountSelect>
          {bankAccounts.map((account) => (
            <BankAccountOption
              key={account.id}
              selected={selectedBankAccount === account.id}
              onClick={() => onBankAccountSelect(account.id)}
            >
              <BankAccountIcon>
                <BankOutlined />
              </BankAccountIcon>
              <BankAccountInfo>
                <BankAccountName>{account.name}</BankAccountName>
                <BankAccountNumber>****{account.mask}</BankAccountNumber>
              </BankAccountInfo>
            </BankAccountOption>
          ))}
        </BankAccountSelect>
      </Form.Item>

      <Form.Item
        label="Email"
        name="email"
        rules={[
          { required: true, message: "Please enter your email" },
          { type: "email", message: "Please enter a valid email" },
        ]}
      >
        <Input placeholder="john@example.com" />
      </Form.Item>

      <ActionButton
        type="primary"
        size="large"
        htmlType="submit"
        loading={loading}
        disabled={!selectedBankAccount}
      >
        Pay ${amount.toFixed(2)}
      </ActionButton>
    </Form>
  );
};

// Wrapper component with Stripe Elements
const PaymentFormWrapper = (props) => (
  <Elements stripe={stripePromise}>
    <PaymentForm {...props} />
  </Elements>
);

// Styled Components
const PaymentFormCard = styled(Card)`
  max-width: 600px;
  margin: 0 auto;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  .ant-card-body {
    padding: 2rem;
  }
`;

const StepTitle = styled.h2`
  color: var(--color-primary);
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  font-family: var(--font-primary);
`;

const StepDescription = styled.p`
  color: var(--color-text-light);
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const PaymentMethodStep = styled.div`
  text-align: center;
`;

const PaymentMethodOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const PaymentMethodOption = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border: 2px solid
    ${(props) =>
      props.selected ? "var(--color-primary)" : "var(--color-border-light)"};
  border-radius: 12px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) =>
      props.disabled ? "var(--color-border-light)" : "var(--color-primary)"};
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.1);
  }
`;

const PaymentMethodIcon = styled.div`
  font-size: 2rem;
  color: var(--color-primary);
  margin-right: 1rem;
`;

const PaymentMethodInfo = styled.div`
  text-align: left;
  flex: 1;
`;

const PaymentMethodName = styled.div`
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
`;

const PaymentMethodDescription = styled.div`
  color: var(--color-text-light);
  font-size: 0.9rem;
`;

const PaymentDetailsStep = styled.div`
  text-align: center;
`;

const PaymentAmount = styled.div`
  background: var(--color-bg-tertiary);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const AmountLabel = styled.div`
  color: var(--color-text-light);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const AmountValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-primary);
`;

const CardElementContainer = styled.div`
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  padding: 1rem;
  background: white;
`;

const BankAccountSelect = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BankAccountOption = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 2px solid
    ${(props) =>
      props.selected ? "var(--color-primary)" : "var(--color-border-light)"};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
  }
`;

const BankAccountIcon = styled.div`
  font-size: 1.5rem;
  color: var(--color-primary);
  margin-right: 1rem;
`;

const BankAccountInfo = styled.div`
  text-align: left;
  flex: 1;
`;

const BankAccountName = styled.div`
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 1rem;
  margin-bottom: 0.25rem;
`;

const BankAccountNumber = styled.div`
  color: var(--color-text-light);
  font-size: 0.9rem;
`;

const ActionButton = styled(Button)`
  height: 48px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  padding: 0 2rem;
`;

const ProcessingStep = styled.div`
  text-align: center;
`;

const ProcessingContent = styled.div`
  padding: 3rem 1rem;
`;

const ProcessingText = styled.div`
  margin-top: 1rem;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-primary);
`;

const ProcessingDescription = styled.div`
  margin-top: 0.5rem;
  color: var(--color-text-light);
  font-size: 1rem;
`;

const SuccessStep = styled.div`
  text-align: center;
`;

const SuccessContent = styled.div`
  padding: 2rem 1rem;
`;

const SuccessTitle = styled.h2`
  color: var(--color-primary);
  font-size: 1.5rem;
  font-weight: 700;
  margin: 1rem 0;
`;

const SuccessDescription = styled.p`
  color: var(--color-text-light);
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const PaymentSummary = styled.div`
  background: var(--color-bg-tertiary);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: left;
  max-width: 300px;
  margin: 0 auto;
`;

const SummaryItem = styled.div`
  margin-bottom: 0.5rem;
  color: var(--color-text-primary);
  font-size: 0.95rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export default PaymentFormWrapper;
